



































































































































































import axios from 'axios';
import moment from 'moment-timezone';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

import { BaseVue } from '@/BaseVue';
import UiAlert from '@/components/ui/UiAlert.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiButtonToggle from '@/components/ui/UiButtonToggle.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import UiDataTable from '@/components/ui/UiDataTable.vue';
import UiDatePicker from '@/components/ui/UiDatePicker.vue';
import UiTruncateText from '@/components/ui/UiTruncateText.vue';
import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';
import { clearDownloadIframes, downloadAuthorizedFile } from '@/utils/downloadFile';
import numberUtils from '@/utils/numberUtils';

import { baConfig } from '../../../../config';
import { ApiSvcInventoryView } from '../../../../generated/api-svc';

type ActionsSummaryGranularity = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'total';

export interface ActionsSummaryLine {
  transaction_period: string;
  subsidiaryName: string;
  category: string;
  asset: string;
  wallet: string;
  contact: string;
  total_asset_unit_adj: number;
  total_cost_basis_acquired: number;
  total_cost_basis_relieved: number;
  total_fair_market_value_disposed: number;
  total_short_term_gain_loss: number;
  total_long_term_gain_loss: number;
  total_impairment_expense: number;
  total_realized_gain_loss: number;
  isTotal?: boolean;
}

@Component({
  components: {
    UiAlert,
    UiButton,
    UiButtonToggle,
    UiCheckbox,
    UiDataTable,
    UiDatePicker,
    UiTruncateText,
  },
})
export default class ActionsSummaryReport extends BaseVue {
  @Prop({ default: null })
  public readonly view!: ApiSvcInventoryView | null;

  public lines: ActionsSummaryLine[] = [];
  public startDate = moment.tz(moment.tz.guess()).subtract(1, 'day').format('YYYY-MM-DD');
  public endDate = moment.tz(moment.tz.guess()).subtract(1, 'day').format('YYYY-MM-DD');
  public granularity: ActionsSummaryGranularity = 'monthly';
  public includeInternalTransfers = false;
  public isLoading = false;
  public csvLoading = false;
  public reportElapsedTime? = '';
  public errorMessage: string | null = null;
  public numFormat = numberUtils.getFormatter({ accountingNegative: true });
  public baseUrl = getClusterAwareBaseUrl(process.env.VUE_APP_RPT_API_URL ?? process.env.VUE_APP_API_URL ?? '');
  public granularityOptions = [
    { value: 'Daily', id: 'daily' },
    { value: 'Weekly', id: 'weekly' },
    { value: 'Monthly', id: 'monthly' },
    { value: 'Yearly', id: 'yearly' },
    { value: 'Total', id: 'total' },
  ];

  downloadFile = downloadAuthorizedFile;

  public get headers() {
    return [
      { id: 'transaction_period', label: 'Period', defaultVisibility: true },
      { id: 'subsidiaryName', label: 'Subsidiary', defaultVisibility: true },
      { id: 'category', label: 'Category', defaultVisibility: true },
      { id: 'asset', label: 'Asset', defaultVisibility: true },
      { id: 'wallet', label: 'Wallet', defaultVisibility: true },
      { id: 'contact', label: 'Contact', defaultVisibility: true },
      {
        id: 'total_asset_unit_adj',
        label: 'Qty Adj',
        defaultVisibility: true,
        textAlignment: 'right',
      },
      {
        id: 'total_cost_basis_acquired',
        label: 'Cost Basis',
        defaultVisibility: true,
        textAlignment: 'right',
        groupLabel: 'Acquired',
      },
      {
        id: 'total_cost_basis_relieved',
        label: 'Cost Basis',
        defaultVisibility: true,
        textAlignment: 'right',
        groupLabel: 'Disposed',
      },
      {
        id: 'total_fair_market_value_disposed',
        label: 'FMV',
        defaultVisibility: true,
        textAlignment: 'right',
        groupLabel: 'Disposed',
      },
      {
        id: 'total_short_term_gain_loss',
        label: 'ST Gain Loss',
        defaultVisibility: true,
        textAlignment: 'right',
        groupLabel: 'Gain Loss',
      },
      {
        id: 'total_long_term_gain_loss',
        label: 'LT Gain Loss',
        defaultVisibility: true,
        textAlignment: 'right',
        groupLabel: 'Gain Loss',
      },
      {
        id: 'total_realized_gain_loss',
        label: 'Realized Gain Loss',
        defaultVisibility: true,
        textAlignment: 'right',
        groupLabel: 'Gain Loss',
      },
      {
        id: 'total_impairment_expense',
        label: 'Impairment Expense',
        defaultVisibility: true,
        textAlignment: 'right',
      },
    ];
  }

  public setGranularity(granularity: string | number) {
    if (
      granularity === 'daily' ||
      granularity === 'weekly' ||
      granularity === 'monthly' ||
      granularity === 'yearly' ||
      granularity === 'total'
    ) {
      this.granularity = granularity;
    }
  }

  public getGainLossClass(gl: number) {
    if (gl > 0) {
      return 'tw-text-green-500';
    } else if (gl < 0) {
      return 'tw-text-red-500';
    } else {
      return '';
    }
  }

  async runReport() {
    if (!this.view?.id) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.reportElapsedTime = undefined;
    const reportStartTime = Date.now();

    try {
      const resp = await axios.get(this.reportUrl, {
        params: this.requestParams(false),
        withCredentials: true,
      });

      if (resp.status === 200) {
        const responseLines = (resp.data.lines || []) as ActionsSummaryLine[];
        this.lines = this.withTotalRow(responseLines);
      }
    } catch (error) {
      this.errorMessage = 'An error occurred while running the report.';
    } finally {
      this.isLoading = false;
      this.reportElapsedTime = this.getElapsedTime(reportStartTime);
    }
  }

  async downloadReport() {
    if (!this.view?.id) {
      return;
    }

    this.csvLoading = true;
    this.errorMessage = null;

    try {
      const resp = await axios.get(this.reportUrl, {
        params: this.requestParams(true),
        withCredentials: true,
      });

      if (resp.status === 200) {
        const exportIds = resp.data.exportIds || [];
        const downloadUrlPromises = exportIds.map((exportId: string) =>
          axios.get(`${this.baseUrl}v2/orgs/${this.$store.state.currentOrg.id}/exports/${exportId}?rawUrl=true`, {
            withCredentials: true,
          })
        );
        clearDownloadIframes();
        const downloadUrls = await Promise.all(downloadUrlPromises);
        downloadUrls.forEach((downloadUrl) => {
          this.downloadFile(downloadUrl.data);
        });
      }
    } catch (error) {
      this.errorMessage = 'An error occurred while downloading the report.';
    } finally {
      this.csvLoading = false;
    }
  }

  private get reportUrl() {
    return `${baConfig.getFriendlyApiUrl()}/orgs/${this.orgId}/inventory-views/${
      this.view?.id ?? ''
    }/reports/actions-summary-report`;
  }

  private requestParams(exportResults: boolean) {
    return {
      startDate: this.startDate,
      endDate: this.endDate,
      granularity: this.granularity,
      includeInternalTransfers: this.includeInternalTransfers,
      exportResults,
    };
  }

  private withTotalRow(lines: ActionsSummaryLine[]) {
    if (lines.length === 0) {
      return [];
    }

    const total = lines.reduce(
      (acc, line) => {
        acc.total_asset_unit_adj += this.toNumber(line.total_asset_unit_adj);
        acc.total_cost_basis_acquired += this.toNumber(line.total_cost_basis_acquired);
        acc.total_cost_basis_relieved += this.toNumber(line.total_cost_basis_relieved);
        acc.total_fair_market_value_disposed += this.toNumber(line.total_fair_market_value_disposed);
        acc.total_short_term_gain_loss += this.toNumber(line.total_short_term_gain_loss);
        acc.total_long_term_gain_loss += this.toNumber(line.total_long_term_gain_loss);
        acc.total_impairment_expense += this.toNumber(line.total_impairment_expense);
        acc.total_realized_gain_loss += this.toNumber(line.total_realized_gain_loss);
        return acc;
      },
      {
        transaction_period: 'TOTAL',
        subsidiaryName: '',
        category: '',
        asset: '',
        wallet: '',
        contact: '',
        total_asset_unit_adj: 0,
        total_cost_basis_acquired: 0,
        total_cost_basis_relieved: 0,
        total_fair_market_value_disposed: 0,
        total_short_term_gain_loss: 0,
        total_long_term_gain_loss: 0,
        total_impairment_expense: 0,
        total_realized_gain_loss: 0,
        isTotal: true,
      }
    );

    return [...lines, total];
  }

  private toNumber(value: string | number | undefined | null) {
    const num = typeof value === 'string' ? Number(value) : value;
    return typeof num === 'number' && !isNaN(num) ? num : 0;
  }
}
